import {
  DECIDED_CANDIDACY_STATES as DECIDED_STATES,
  type CandidacyState,
  type DecisionRecord,
} from "@talenthop/shared";
import { ApiError } from "../../lib/api.js";

/**
 * The two pure layers behind the recruiter's Advance / Reject panel, pulled out of Review.tsx so
 * they can be unit-tested.
 *
 * Both exist because the panel used to answer "has this been decided?" from a DIFFERENT source
 * than the server gates on. `POST /candidacies/{id}/decision` refuses a second decision based on
 * candidacies.state; the UI inferred it from the decision RECORDS. When the two disagreed — most
 * commonly a reject that committed but whose response never made it back to the browser — the
 * buttons stayed live on a candidacy the API would only ever 409, and the 409 was rendered as the
 * generic "please retry in a moment", so a reviewer retried forever and could not tell which
 * decision was actually on record.
 *
 * `DECIDED_STATES` used to be a second, hand-copied literal here, kept in step with
 * `apps/api/src/functions/decision.ts`'s own copy only by a static test scraping both files' text.
 * Both now import the one definition, `DECIDED_CANDIDACY_STATES` (packages/shared/src/types.ts) —
 * a third copy (apps/worker/src/saga.ts rescoreCandidacy) reads the same constant, so there is
 * exactly one array literal for "has this candidate already been decided" in the whole codebase. */

export interface SettledDecision {
  outcome: "advanced" | "rejected";
  /** Reviewer-facing name for the outcome the server holds. */
  label: string;
  /** Reason + timestamp of the human decision behind it, when one is on record. */
  reason: string | null;
  at: string | null;
}

/**
 * The decision the SERVER holds, or null while the candidacy is still open. `state` is the
 * authority (it is the field the endpoint's conflict guard reads); the records only supply the
 * reason and timestamp to show alongside it.
 */
export function settledDecision(
  state: CandidacyState | undefined,
  decisions: readonly DecisionRecord[] | undefined,
): SettledDecision | null {
  if (!state || !DECIDED_STATES.has(state)) return null;
  const outcome = state === "cut_notified" ? "rejected" : "advanced";
  // provenance='human' is not optional here: the worker inserts an 'advance' row per SCORE
  // (saga.ts) as a ranking recommendation, so an unfiltered scan attributes a machine ranking to
  // a person. An 'override' row means the trail was consciously overturned — the row AFTER it
  // carries the decision that actually stands, which is why this takes the last match, not the
  // first.
  const actions: DecisionRecord["action"][] =
    outcome === "rejected" ? ["proposed_cut", "cut_notified"] : ["advance"];
  const human = (decisions ?? []).filter(
    (d) => d.provenance === "human" && actions.includes(d.action),
  );
  const last = human.length > 0 ? human[human.length - 1]! : null;
  return {
    outcome,
    label: outcome === "rejected" ? "Rejected" : "Advanced to hire",
    reason: last?.reason || null,
    at: last?.ts || null,
  };
}

/**
 * Copy for a failed decision attempt. Keyed on the action that failed, because the same mutation
 * serves both buttons — the old single string told someone whose Advance had failed that a
 * rejection reason was required.
 */
export function decisionErrorMessage(
  error: unknown,
  action: "advance" | "reject" | undefined,
): string | null {
  if (!error) return null;
  const status = error instanceof ApiError ? error.status : null;
  // 409 is the one status that is NOT worth retrying: a decision is already on record and the
  // API will refuse every repeat. Saying "retry in a moment" here is what kept the panel showing
  // failure feedback for a decision the server had already accepted.
  if (status === 409) {
    return "A decision is already on record for this candidate, so this one was not applied. The Decision panel now shows the decision the server holds.";
  }
  if (status === 403) {
    return "Your role cannot record hiring decisions on this candidate.";
  }
  if (status === 400) {
    return action === "reject"
      ? "The API rejected the decision — a written reason (min 4 chars) is required to reject."
      : "The API rejected the decision — the request was not valid.";
  }
  return "Couldn't record the decision. Please retry in a moment.";
}
